import { G } from '@mobily/ts-belt';
import { Box, Stack, Typography } from '@mui/material';
import { ReactNode } from 'react';
import { Link, useTranslate } from 'react-admin';

import {
  GetB2bEmployeesBulkUploadErrorsOneResponse,
  GetB2bEmployeesBulkUploadErrorsResponse,
} from '@Common/types/bo/b2b/employees/bulk_upload';

import { Status } from '@sh4res/bo-common/components/B2bEmployeeBulkCreate/components/bulkUploadStatus';
import { useResourceBasePath } from '@sh4res/bo-common/hooks';
import { buildLink } from '@sh4res/bo-common/utils/build_link';
import { translateBackendError } from '@sh4res/bo-common/utils/translate_error';

interface BulkUploadErrorsListProps {
  hasError: boolean;
  errorColor: string;
  errors: GetB2bEmployeesBulkUploadErrorsResponse | null;
  statuses: Status[];
  renderCompanyLabel?: (error: GetB2bEmployeesBulkUploadErrorsOneResponse) => ReactNode;
}

export function BulkUploadErrorsList({
  hasError,
  errorColor,
  errors,
  statuses,
  renderCompanyLabel,
}: BulkUploadErrorsListProps) {
  const translate = useTranslate();
  const resourceBasePath = useResourceBasePath();

  return (
    <Stack
      direction="column"
      spacing={1}
      sx={{
        border: `2px solid ${errorColor}`,
        borderRadius: 1,
        padding: 1,
        color: errorColor,
        alignItems: 'center',
        justifyContent: 'center',
        overflowX: 'auto',
      }}>
      {hasError && G.isNotNullable(errors)
        ? errors.map((bulkUploadError) => {
            const errorAction =
              statuses.find(
                (status) =>
                  status.name ===
                  (G.isNullable(bulkUploadError.b2b_employee_id) ? 'created' : 'updated'),
              ) ?? statuses[0];

            return (
              <Stack
                key={bulkUploadError.id}
                direction="row"
                spacing={1}
                alignItems={'center'}
                width={'100%'}>
                <Typography variant="subtitle1" sx={{ whiteSpace: 'nowrap' }}>
                  {translate(
                    'resources.bo_common.b2b.employees.actions.bulk_create.sections.results.line',
                    { line: bulkUploadError.file_line_number },
                  )}
                </Typography>
                <Typography variant="subtitle1">-</Typography>
                {renderCompanyLabel && (
                  <>
                    <Typography variant="subtitle1" sx={{ whiteSpace: 'nowrap' }}>
                      {renderCompanyLabel(bulkUploadError)}
                    </Typography>
                    <Typography variant="subtitle1">-</Typography>
                  </>
                )}
                <Box
                  sx={{
                    color: 'white',
                    fontSize: '10px',
                    backgroundColor: errorAction.color,
                    padding: 1,
                    borderRadius: 1,
                  }}>
                  {translate(
                    `resources.bo_common.b2b.employees.actions.bulk_create.sections.results.actions.${errorAction.name}`,
                  )}
                </Box>
                <Typography variant="subtitle1">-</Typography>
                {errorAction.name === 'updated' && (
                  <Link
                    to={{
                      pathname: buildLink(
                        `${resourceBasePath}employees`,
                        'show',
                        String(bulkUploadError.b2b_employee_id),
                      ),
                    }}
                    target="_blank"
                    sx={{ textDecoration: 'underline' }}>
                    {bulkUploadError.employee_nir ?? bulkUploadError.employee_ntt}
                  </Link>
                )}
                {errorAction.name === 'created' && (
                  <Typography variant="subtitle1">
                    {bulkUploadError.employee_nir ?? bulkUploadError.employee_ntt}
                  </Typography>
                )}
                <Typography variant="subtitle1">-</Typography>
                <Typography variant="subtitle1" sx={{ whiteSpace: 'nowrap' }}>
                  {translateBackendError(
                    {
                      resource:
                        'resources.bo_common.b2b.employees.actions.bulk_create.sections.results.errors',
                      error: bulkUploadError.error,
                    },
                    (translation) => translate(translation),
                  )}
                </Typography>
              </Stack>
            );
          })
        : translate(
            'resources.bo_common.b2b.employees.actions.bulk_create.sections.results.no_error',
          )}
    </Stack>
  );
}
